import { ReactNode, SyntheticEvent, useCallback, useRef, useState } from 'react';
import cardValidator from 'card-validator';
import _ from 'lodash';

import { validationSchema } from './validations';
import { setNestedObjectValues, useFormik } from 'formik';
import { useMediaQuery, useTheme } from '@mui/material';
import { Button, Checkbox, Input, Tooltip } from '@/shared/ui';
import { useAppSelector } from '@/shared/hooks/store';
import { selectUserProfileInfo } from '@/store/slices/onboarding/selectors';
import { creditCardIcons } from '@/shared/constants/main';
import { ManualAddressFormElements } from '@/shared/components/manual-address-form-elements';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { IPaymentFormData, paymentFormInitialBillingDetails, paymentFormInitialValues } from './constants';

import CloseIcon from '@/assets/icons/x-icon-black.svg';
import HippacIcon from '@/assets/images/onboarding/hipaa.png';
import EncryptionIcon from '@/assets/images/onboarding/encryption.png';
import CardsLineImg from '@/assets/images/onboarding/cards-line.png';
import CardShadow from '@/assets/icons/credit-card/card-shadow.svg';
import CvcIcon from '@/assets/icons/credit-card/cvc.svg';

import './style.css';

const buildDisplayCardNumber = (rawValue: string) => {
    let buildString = rawValue;
    const gaps = cardValidator.number(rawValue)?.card?.gaps;
    if (gaps) {
        buildString = addCardGapsBasedOnValidatedCard(rawValue, gaps);
    }

    return buildString;
};

const removeSpacesFromCardNumber = (cardNumber: string) => {
    return cardNumber.replace(/\s/g, '');
};

const addCardGapsBasedOnValidatedCard = (stringVal: string, gaps: Array<number>) => {
    const stringWithoutSpaces = stringVal.replace(/\s/g, '');
    const sections: string[] = [];
    for (let i = 0; i < gaps.length; i++) {
        if (i === 0) {
            sections.push(stringWithoutSpaces.slice(0, gaps[i]));
        } else {
            sections.push(stringWithoutSpaces.slice(gaps[i - 1], gaps[i]));
        }
    }
    sections.push(stringWithoutSpaces.slice(gaps[gaps.length - 1]));
    return sections.filter(section => !!section).join(' ');
};

const fields = [
    { name: 'firstName', queryClassName: 'payment__form_first_name' },
    { name: 'lastName', queryClassName: 'payment__form_last_name' },
    { name: 'cardNumber', queryClassName: 'payment__form_card_number' },
    { name: 'expirationDate', queryClassName: 'payment__form_expiration_date' },
    { name: 'cvv', queryClassName: 'payment__form_cvv' },
    { name: 'addressLine1', queryClassName: 'manual_address__form_elements__address' },
    { name: 'city', queryClassName: 'manual_address__form_elements__city' },
    { name: 'state', queryClassName: 'manual_address__form_elements__state' },
    { name: 'zip', queryClassName: 'manual_address__form_elements__zip' }
];

const defaultAwarenessText = 'By saving your payment method, you authorize MedBox to apply recurring charges to your credit/debit card for each billing period.';

export const PaymentForm = ({
    onSave,
    addPaymentLoading,
    userAddress,
    showCardIcons = false,
    hideIconsFromInput = false,
    header,
    errorMessage,
    required = true,
    isPreferred = false,
    cancelAddPaymentHandler,
    cancelButtonText = 'Add Payment Later',
    headerOffset = 0,
    withinModal = true,
    className = '',
    formikControl,
    awarenessText = defaultAwarenessText,
}: {
    onSave?: (formData: IPaymentFormData) => void;
    addPaymentLoading: boolean;
    userAddress: string;
    hasFooter?: boolean;
    showCancel?: boolean;
    cancelAddPaymentHandler?: () => void;
    errorMessage?: string;
    cancelButtonText?: string;
    showCardIcons?: boolean;
    hideIconsFromInput?: boolean;
    header?: ReactNode;
    required?: boolean;
    saveButton?: ReactNode;
    withDefaultPayment?: boolean;
    isPreferred?: boolean;
    headerOffset?: number;
    withinModal?: boolean;
    className?: string;
    awarenessText?: string;
    formikControl?: ReturnType<typeof useFormik<IPaymentFormData>>;
}) => {
    const ref = useRef(null);
    const theme = useTheme();
    const isDownXL = useMediaQuery(theme.breakpoints.down('xl'));
    const userProfile = useAppSelector(selectUserProfileInfo);
    const [showCVVPopover, setShowCVVPopover] = useState(false);
    const [showBillingForm, setShowBillingForm] = useState(false);

    const initialVals = {
        ...paymentFormInitialValues,
        preferred: isPreferred,
        firstName: userProfile?.firstName || '',
        lastName: userProfile?.lastName || '',
    };

    const formik = formikControl || useFormik({
        initialValues: initialVals,
        validationSchema,
        onSubmit: values => {
            onSave?.(values);
        }
    });

    const scrollToInvalidField = useCallback(async () => {
        const errors = await formik.validateForm();
        if (!Object.keys(errors).length) return;

        const errorFields = { ...errors, ...errors.billingDetails };
        const fieldWithError = fields.find(field => Object.keys(errorFields).includes(field.name));
        if (fieldWithError) {
            const element = document.querySelector(`.${fieldWithError.queryClassName}`);
            if (element) {
                if (headerOffset && !withinModal) {
                    const elementPosition = element.getBoundingClientRect().top + window.scrollY - headerOffset;
                    window.scrollTo({
                        top: elementPosition,
                        behavior: 'smooth'
                    });
                } else {
                    document.querySelector(`.${fieldWithError.queryClassName}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }
    }, [formik, headerOffset, withinModal]);

    const handleBillingFormChange = useCallback(
        (field: string, value: string) => {
            formik.setFieldValue('billingDetails', { ...formik.values.billingDetails, [field]: value });
        },
        [formik]
    );

    const handleHasDifferentBillingAddress = useCallback(() => {
        const newValue = formik.values.hasDifferentBillingAddress === 1 ? 2 : 1;
        formik.setFieldValue('hasDifferentBillingAddress', newValue);
        const hasDiffAddress = newValue === 2;

        setShowBillingForm(hasDiffAddress);

        if (hasDiffAddress) {
            formik.setFieldValue('billingDetails', {
                ...paymentFormInitialBillingDetails
            });
        }
    }, [formik]);

    const handleSubmit = async (e: SyntheticEvent) => {
        e.preventDefault();
        const errors = await formik.validateForm();

        if (!_.isEmpty(errors)) {
            formik.setErrors(errors);
            formik.setTouched(setNestedObjectValues(errors, true));

            return;
        }

        onSave?.(formik.values);
    }

    const buttonClassName = 'allergies-and-conditions-radio-button';

    return (
        <form onSubmit={handleSubmit} className={`flex flex-col gap-4 ${className}`}>
            {showCardIcons && !header ? (
                <img src={CardsLineImg} alt="" className="max-w-[303px] xl:hidden ms:mx-auto" />
            ) : (
                header
            )}

            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:gap-5 lg:gap-7">
                <Input
                    className="payment__form_first_name"
                    name="firstName"
                    label="First Name"
                    value={formik.values?.firstName}
                    required
                    placeholder="ex. Jane"
                    error={formik.touched?.firstName && !!formik.errors?.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={formik.errors?.firstName}
                    labelClassName="font-atkinsonHyperlegible"
                    testId="payment-form-first-name"
                />
                <div className="flex flex-col gap-1">
                    <Input
                        className="payment__form_last_name"
                        name="lastName"
                        label="Last Name"
                        value={formik.values?.lastName}
                        required
                        placeholder="ex. Doh"
                        error={formik.touched?.lastName && !!formik.errors?.lastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.errors?.lastName}
                        labelClassName="font-atkinsonHyperlegible"
                        testId="payment-form-last-name"
                    />
                </div>
            </div>

            <div className="relative">
                <Input
                    className="payment__form_card_number"
                    name="cardNumber"
                    label="Credit / Debit Card Number"
                    value={buildDisplayCardNumber(formik.values.cardNumber)}
                    required={required}
                    placeholder="ex. 1234 1234 1234 123"
                    error={formik.touched.cardNumber && !!formik.errors.cardNumber}
                    testId="payment-form-card-number"
                    onChange={e => {
                        const { value } = e.target;
                        const regex = /^[0-9\s]*$/;
                        if (!regex.test(e.target.value)) {
                            return;
                        }
                        const cardNumber = removeSpacesFromCardNumber(value);
                        const validator = cardValidator.number(cardNumber);
                        if (validator?.card?.type === 'american-express') {
                            if (cardNumber.length > 15) {
                                return;
                            }
                        } else {
                            if (cardNumber.length > 16) {
                                return;
                            }
                        }
                        e.target.value = cardNumber;
                        formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    errorMessage={formik.errors.cardNumber}
                    labelClassName="font-atkinsonHyperlegible"
                    endContent={!hideIconsFromInput && !header && <CardIcons />}
                    startContent={<img src={CardShadow} />}
                />
            </div>
            <div className="grid gap-2.5 grid-cols-2 md:gap-5 lg:gap-7">
                <Input
                    className="payment__form_expiration_date"
                    testId="payment-form-expiration-date"
                    name="expirationDate"
                    label="Expires"
                    value={formik.values.expirationDate}
                    required={required}
                    placeholder="ex. 05/26"
                    error={formik.touched.expirationDate && !!formik.errors.expirationDate}
                    errorMessage={formik.errors.expirationDate}
                    onBlur={formik.handleBlur}
                    onChange={e => {
                        const { value } = e.target;
                        const regex = /^(?:\d{2}\/\d{0,2}|\d{3}|\d{2}|\d)?$|^$/;
                        if (value.length <= 5 && regex.test(value)) {
                            if (value.length === 3 && !value.includes('/')) {
                                e.target.value = value.slice(0, 2) + '/' + value.slice(2);
                                formik.handleChange(e);
                            } else {
                                formik.handleChange(e);
                            }
                        }
                    }}
                    labelClassName="font-atkinsonHyperlegible"
                />
                <div className="flex flex-col gap-1">
                    <Input
                        className="payment__form_cvv"
                        testId="payment-form-cvv"
                        name="cvv"
                        label={<>
                            CVV/CVC
                            {!isDownXL && (
                                <a
                                    ref={ref}
                                    onClick={() => setShowCVVPopover(true)}
                                    className="truncate ml-[0.5625rem] cursor-pointer font-atkinsonHyperlegible text-[15px] text-primary underline"
                                >
                                    What is CVV/CVC?
                                </a>
                            )}
                        </>}
                        value={formik.values.cvv}
                        required={required}
                        placeholder="ex. 456"
                        error={formik.touched.cvv && !!formik.errors.cvv}
                        onChange={e => {
                            const { value } = e.target;
                            const regex = /^\d*$/;
                            if (value.length <= 4 && regex.test(value)) {
                                formik.handleChange(e);
                            }
                        }}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.errors.cvv}
                        labelClassName="font-atkinsonHyperlegible overflow-hidden"
                        endContent={<img src={CvcIcon} className="xs-max:hidden" />}
                    />
                    {isDownXL && (
                        <a
                            ref={ref}
                            onClick={() => setShowCVVPopover(true)}
                            className="ml-auto mt-4 cursor-pointer font-atkinsonHyperlegible text-[14px] text-primary underline md:text-lg"
                        >
                            What is CVV/CVC?
                        </a>
                    )}


                    <Tooltip
                        customPosition={isDownXL ? 'bottom-left' : 'top-right'}
                        wrapperClassName="cvv-tooltip"
                        hasCloseIcon={false}
                        open={showCVVPopover}
                        anchorEl={ref.current}
                        onClose={() => setShowCVVPopover(false)}
                    >
                        <div
                            onClick={() => setShowCVVPopover(false)}
                            className="p-[5px] cursor-pointer min-w-[1.1875rem] h-[1.1875rem] flex justify-center items-center bg-mint-400 rounded-full"
                        >
                            <img src={CloseIcon} alt="" width={10} className="w-full" />
                        </div>
                        <div className="text-xs ml-[0.5625rem] font-atkinsonHyperlegible">
                            <span className="font-atkinsonBold">CVV/CVC</span>
                            &nbsp;
                            number is the last three digits at the back of your card. For American Express cards, the CVV is a 4-digit number on the front of the card.
                        </div>
                    </Tooltip>
                </div>
            </div>

            <Checkbox
                className={`payment__form_same_address ${formik.values.hasDifferentBillingAddress === 1 ? 'payment__form_checkbox_on' : ''}`}
                label={
                    <div className="font-atkinsonHyperlegible text-sm lg:text-lg">
                        <span>
                            Billing address is the same as the shipping address <strong>({userAddress})</strong>
                        </span>
                    </div>
                }
                checked={formik.values.hasDifferentBillingAddress === 1}
                onClick={handleHasDifferentBillingAddress}
                labelClassName={`${buttonClassName}-option  ${buttonClassName}-checkbox ${!formik.values.hasDifferentBillingAddress ? 'checked' : ''}`}
            />
            <Checkbox
                className={`payment__form_different_address ${formik.values.hasDifferentBillingAddress === 2 ? 'payment__form_checkbox_on' : ''}`}
                label={
                    <div className="font-atkinsonHyperlegible text-sm lg:text-lg">
                        <span>I have a different billing address for this card</span>
                    </div>
                }
                checked={formik.values.hasDifferentBillingAddress === 2}
                onClick={handleHasDifferentBillingAddress}
                labelClassName={`${buttonClassName}-option  ${buttonClassName}-checkbox ${!formik.values.hasDifferentBillingAddress ? 'checked' : ''}`}
            />

            {showBillingForm ? (
                <>
                    <ManualAddressFormElements
                        handleChange={handleBillingFormChange}
                        values={formik.values.billingDetails || paymentFormInitialBillingDetails}
                        errors={formik.errors.billingDetails || {}}
                        touched={formik.touched.billingDetails || {}}
                        showApartmentSuiteBoxNumberField
                        formTitle="Billing Address"
                        formTitleClassName="font-atkinsonBold !text-xl mb-0 mt-1 "
                    />
                </>
            ) : null}

            {awarenessText && (
                <p className="py-1 text-left font-atkinsonHyperlegible text-sm lg:py-2 lg:text-lg">
                    {awarenessText}
                </p>
            )}
            {!isPreferred && (
                <Checkbox
                    label={
                        <div className="font-atkinsonHyperlegible text-sm lg:text-lg">
                            <span>Make this your default payment method</span>
                        </div>
                    }
                    checked={formik.values.preferred}
                    onClick={() => formik.setFieldValue('preferred', !formik.values.preferred)}
                    labelClassName={`${buttonClassName}-option  ${buttonClassName}-checkbox ${formik.values.preferred ? 'checked' : ''}`}
                />
            )}

            <Button
                className="payment__form_save_payment_btn"
                type="submit"
                testId="button-save-payment"
                loading={addPaymentLoading}
                disabled={addPaymentLoading}
                showEndIcon
                sx={{
                    boxShadow: '0px 4px 7.1px 0px #0000000F'
                }}
                onClick={scrollToInvalidField}
            >
                Save Payment
            </Button>

            {cancelAddPaymentHandler && (
                <Button
                    testId="button-cancel-payment"
                    onClick={cancelAddPaymentHandler}
                    sx={{
                        width: '100%',
                        [theme.breakpoints.down('md')]: {
                            mt: '10px'
                        },
                        fontFamily: 'DM Sans medium'
                    }}
                    theme="mintNoHoverReaction"
                    variant="outlined"
                    disabled={addPaymentLoading}
                >
                    {cancelButtonText}
                </Button>
            )}

            <div className="hidden flex-wrap items-center justify-center gap-3 pt-3 lg:flex lg:gap-4">
                <img src={HippacIcon} alt="" className="h-10 max-w-36 sm:h-auto" />
                <img src={EncryptionIcon} alt="" className="h-10 max-w-36 sm:h-auto" />
            </div>
        </form>
    );
};

const CardIcons = () => {
    return (
        <div className="flex gap-1 lg:gap-2 xl-max:hidden">
            {Object.values(creditCardIcons).map((icon, idx) => (
                <img src={icon} key={idx} className="max-h-6 w-full" />
            ))}
        </div>
    );
};

export default withScrollRestoration(PaymentForm);
